// @vendors
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

// @helpers
import { recordLinkV2, recordProcess } from 'utilities/tagging';
import migrationContractualPollingActions from '../../../actions/migrationContractualPolling';
import { goToGP } from '../../../utilities/migration.utils';

// @constants
import { analyticsId, catalogue } from '../analytics/terms.tracking';
import {
  MIGRATION_STATE,
  CALL_STATUS,
  API_MIGRATION_STATE,
} from '../../../migrationUsersBranchView.constants';

// @actions
import { initMigration, verifyOtpPrecontractualConsents } from '../../../actions/migration';
import { resetContractualPolling } from 'actions/contractualPolling';

const page = '/migration/step01/signContract';

export const useTerms = handleChangeMigrationState => {
  const [documentReaded, setDocumentReaded] = useState(false);
  const [contratualInfoChecked, setContratualInfoChecked] = useState(false);
  const [dataProtectionChecked, setDataProtectionChecked] = useState(false);
  const [optionalConsentChecked, setOptionalConsentChecked] = useState(false);
  const [sendConsentsStatus, setSendConsentsStatus] = useState('');
  const [documentsStatus, setDocumentsStatus] = useState('');
  const [error, setError] = useState('');
  const [sendMigrationStarted, setSendMigrationStarted] = useState(false);
  const [operationProcessId, setOperationProcessId] = useState('');
  const immMigration = useSelector(state => state.migration);
  const immPdfData = useSelector(state => state.precontractualsMergePdfs);
  const signaturePattern = useSelector(state => state.signaturePattern);
  const state = useSelector(state => state);
  const immContractualPolling = useSelector(state => state.contractualPolling);
  const contractualError = immContractualPolling.get('documents').get('error');
  const dispatch = useDispatch();
  const history = useHistory();

  const handleGenerateDocuments = () => {
    setDocumentsStatus(CALL_STATUS.IN_PROGRESS);
    dispatch(resetContractualPolling());
    migrationContractualPollingActions.migrationContractualPollingInitialization(
      dispatch,
      immMigration.getIn(['gpData']).toJS(),
      id => {
        setOperationProcessId(id);
        setDocumentsStatus(CALL_STATUS.SUCCESS);
      },
      error => {
        setError(error);
        setDocumentsStatus(CALL_STATUS.ERROR);
      }
    );
  };

  const sendDataAndGetDocs = async () => {
    if (
      !sendMigrationStarted &&
      ![API_MIGRATION_STATE.MIGRATION_STARTED, API_MIGRATION_STATE.VIDEOCALL_KO].includes(
        immMigration.getIn(['status'])
      )
    ) {
      initMigration(dispatch)
        .then(() => {
          setSendMigrationStarted(true);
          handleGenerateDocuments();
        })
        .catch(error => {
          setError(error);
        });
    } else {
      handleGenerateDocuments();
    }
  };

  const handleSendConsentsStatus = (status, error = '') => {
    setSendConsentsStatus(status);
    if (error) {
      setError(error);
    }
    if (status === CALL_STATUS.SUCCESS) {
      handleChangeMigrationState(MIGRATION_STATE.VIDEOCALL_INFO);
    }
  };

  useEffect(() => {
    sendDataAndGetDocs();
  }, []);

  useEffect(() => {
    if (immPdfData.get('errorContent')) {
      recordProcess(
        'proc-migracion',
        'error',
        catalogue[analyticsId.TERMS_ERROR_VIEW](immPdfData.getIn(['errorContent', 'text'])),
        true
      );
    } else if (
      !([sendConsentsStatus, documentsStatus].includes(CALL_STATUS.ERROR) || contractualError)
    ) {
      window.utag_last_name = page;
      recordProcess('proc-migracion', 'paso_1', catalogue[analyticsId.TERMS_VIEW](), true);
    }
  }, [immPdfData, sendConsentsStatus, documentsStatus, contractualError]);

  useEffect(() => {
    if (
      ![
        API_MIGRATION_STATE.ALLOW_MIGRATION,
        API_MIGRATION_STATE.VIDEOCALL_KO,
        API_MIGRATION_STATE.MIGRATION_STARTED,
        API_MIGRATION_STATE.REJECTED_MIGRATION,
      ].includes(immMigration.getIn(['status']))
    ) {
      if (immMigration.getIn(['error'])) {
        handleSendConsentsStatus(CALL_STATUS.ERROR, immMigration.getIn(['error']));
      } else if (immMigration.getIn(['status']) === API_MIGRATION_STATE.VIDEOCALL_SUCCESS) {
        goToGP(history);
      } else {
        if (!error) {
          handleSendConsentsStatus(CALL_STATUS.SUCCESS);
          handleChangeMigrationState(MIGRATION_STATE.VIDEOCALL_INFO);
        }
      }
    }
  }, [immMigration.getIn(['status'])]);

  const resetStatus = () => {
    setSendConsentsStatus('');
  };

  const resetRetryStatus = () => {
    setError('');
    setSendConsentsStatus('');
    setDocumentsStatus('');
    setDocumentReaded(false);
    setContratualInfoChecked(false);
    setDataProtectionChecked(false);
    setOptionalConsentChecked(false);
  };

  const toggleCheckContractualInfo = () => {
    recordLinkV2(catalogue[analyticsId.TOGGLE_CONTRANCT](!contratualInfoChecked));
    if (contratualInfoChecked) {
      resetStatus();
    }
    setContratualInfoChecked(!contratualInfoChecked);
  };

  const toggleCheckDataProtection = () => {
    recordLinkV2(catalogue[analyticsId.TOGGLE_DATA_PROTECTION](!dataProtectionChecked));
    if (dataProtectionChecked) {
      resetStatus();
    }
    setDataProtectionChecked(!dataProtectionChecked);
  };

  const toggleOptionalConsent = () => {
    recordLinkV2(catalogue[analyticsId.TOGGLE_COMMERCIAL_COMMS](!optionalConsentChecked));
    if (optionalConsentChecked) {
      resetStatus();
    }
    setOptionalConsentChecked(!optionalConsentChecked);
  };

  const handleDocumentReaded = () => {
    setDocumentReaded(true);
  };

  const handleFinishRetry = () => {
    recordLinkV2(catalogue[analyticsId.CLICK_RETRY_BUTTON]());
    resetRetryStatus();
    sendDataAndGetDocs();
  };

  const handleFinish = () => {
    verifyOtpPrecontractualConsents(
      dispatch,
      () => state,
      handleSendConsentsStatus,
      optionalConsentChecked,
      operationProcessId
    );
  };

  return {
    contratualInfoChecked,
    dataProtectionChecked,
    documentReaded,
    documentsStatus,
    error,
    handleDocumentReaded,
    handleFinish,
    handleFinishRetry,
    optionalConsentChecked,
    sendConsentsStatus,
    sendDataAndGetDocs,
    signaturePattern,
    toggleCheckContractualInfo,
    toggleCheckDataProtection,
    toggleOptionalConsent,
  };
};
